import type { Stroke, StampType } from './types'

interface RenderArgs {
  ctx: CanvasRenderingContext2D
  stroke: Stroke
  canvasWidth: number
  canvasHeight: number
}

// Convert a normalized [0..1] point into canvas pixels.
function denorm(p: { x: number; y: number }, w: number, h: number) {
  return { x: p.x * w, y: p.y * h }
}

export function renderStroke({ ctx, stroke, canvasWidth, canvasHeight }: RenderArgs) {
  const { kind, color, brushWidth } = stroke
  ctx.save()
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.strokeStyle = color
  ctx.fillStyle = color
  ctx.lineWidth = brushWidth

  switch (kind) {
    case 'pen':
    case 'eraser': {
      // Eraser draws with destination-out composite so it visually wipes
      // underlying strokes on every viewer's canvas. Late-joiners get
      // the eraser stroke in their replay and apply it in stroke-order
      // for the same visual result.
      if (kind === 'eraser') {
        ctx.globalCompositeOperation = 'destination-out'
        ctx.strokeStyle = 'rgba(0,0,0,1)'
      }
      const pts = stroke.points ?? []
      if (pts.length < 2) break
      ctx.beginPath()
      const first = denorm(pts[0], canvasWidth, canvasHeight)
      ctx.moveTo(first.x, first.y)
      for (let i = 1; i < pts.length; i++) {
        const p = denorm(pts[i], canvasWidth, canvasHeight)
        ctx.lineTo(p.x, p.y)
      }
      ctx.stroke()
      break
    }
    case 'highlighter': {
      const pts = stroke.points ?? []
      if (pts.length < 2) break
      ctx.globalAlpha = 0.4
      ctx.lineWidth = brushWidth * 4
      ctx.beginPath()
      const first = denorm(pts[0], canvasWidth, canvasHeight)
      ctx.moveTo(first.x, first.y)
      for (let i = 1; i < pts.length; i++) {
        const p = denorm(pts[i], canvasWidth, canvasHeight)
        ctx.lineTo(p.x, p.y)
      }
      ctx.stroke()
      break
    }
    case 'line': {
      const g = stroke.geometry
      if (!g) break
      const a = denorm({ x: g.x1, y: g.y1 }, canvasWidth, canvasHeight)
      const b = denorm({ x: g.x2, y: g.y2 }, canvasWidth, canvasHeight)
      ctx.beginPath()
      ctx.moveTo(a.x, a.y)
      ctx.lineTo(b.x, b.y)
      ctx.stroke()
      break
    }
    case 'arrow': {
      const g = stroke.geometry
      if (!g) break
      const a = denorm({ x: g.x1, y: g.y1 }, canvasWidth, canvasHeight)
      const b = denorm({ x: g.x2, y: g.y2 }, canvasWidth, canvasHeight)
      ctx.beginPath()
      ctx.moveTo(a.x, a.y)
      ctx.lineTo(b.x, b.y)
      ctx.stroke()
      // arrowhead
      const angle = Math.atan2(b.y - a.y, b.x - a.x)
      const head = Math.max(10, brushWidth * 3)
      ctx.beginPath()
      ctx.moveTo(b.x, b.y)
      ctx.lineTo(b.x - head * Math.cos(angle - Math.PI / 6), b.y - head * Math.sin(angle - Math.PI / 6))
      ctx.lineTo(b.x - head * Math.cos(angle + Math.PI / 6), b.y - head * Math.sin(angle + Math.PI / 6))
      ctx.closePath()
      ctx.fill()
      break
    }
    case 'rect': {
      const g = stroke.geometry
      if (!g) break
      const a = denorm({ x: g.x1, y: g.y1 }, canvasWidth, canvasHeight)
      const b = denorm({ x: g.x2, y: g.y2 }, canvasWidth, canvasHeight)
      ctx.strokeRect(Math.min(a.x, b.x), Math.min(a.y, b.y), Math.abs(b.x - a.x), Math.abs(b.y - a.y))
      break
    }
    case 'oval': {
      const g = stroke.geometry
      if (!g) break
      const a = denorm({ x: g.x1, y: g.y1 }, canvasWidth, canvasHeight)
      const b = denorm({ x: g.x2, y: g.y2 }, canvasWidth, canvasHeight)
      const cx = (a.x + b.x) / 2
      const cy = (a.y + b.y) / 2
      const rx = Math.abs(b.x - a.x) / 2
      const ry = Math.abs(b.y - a.y) / 2
      ctx.beginPath()
      ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2)
      ctx.stroke()
      break
    }
    case 'text': {
      const t = stroke.text
      if (!t) break
      const p = denorm({ x: t.x, y: t.y }, canvasWidth, canvasHeight)
      // fontSize is in absolute px relative to sharer's monitor — scale by canvas vs 1080p heuristic.
      const scale = canvasHeight / 1080
      const size = Math.max(10, t.fontSize * scale)
      ctx.font = `${size}px sans-serif`
      ctx.textBaseline = 'top'
      ctx.fillText(t.value, p.x, p.y)
      break
    }
    case 'stamp': {
      const g = stroke.geometry
      const stampType = stroke.stampType
      if (!g || !stampType) break
      const p = denorm({ x: g.x1, y: g.y1 }, canvasWidth, canvasHeight)
      drawStamp(ctx, stampType, p.x, p.y, Math.max(20, brushWidth * 8))
      break
    }
  }
  ctx.restore()
}

function drawStamp(ctx: CanvasRenderingContext2D, type: StampType, x: number, y: number, size: number) {
  ctx.save()
  ctx.translate(x, y)
  const r = size / 2
  ctx.beginPath()
  switch (type) {
    case 'check':
      ctx.lineWidth = Math.max(2, size / 8)
      ctx.moveTo(-r * 0.5, 0)
      ctx.lineTo(-r * 0.1, r * 0.4)
      ctx.lineTo(r * 0.6, -r * 0.5)
      ctx.stroke()
      break
    case 'x':
      ctx.lineWidth = Math.max(2, size / 8)
      ctx.moveTo(-r * 0.5, -r * 0.5); ctx.lineTo(r * 0.5, r * 0.5)
      ctx.moveTo(r * 0.5, -r * 0.5); ctx.lineTo(-r * 0.5, r * 0.5)
      ctx.stroke()
      break
    case 'star': {
      const spikes = 5
      const outer = r
      const inner = r * 0.5
      let rot = -Math.PI / 2
      const step = Math.PI / spikes
      ctx.moveTo(Math.cos(rot) * outer, Math.sin(rot) * outer)
      for (let i = 0; i < spikes; i++) {
        rot += step
        ctx.lineTo(Math.cos(rot) * inner, Math.sin(rot) * inner)
        rot += step
        ctx.lineTo(Math.cos(rot) * outer, Math.sin(rot) * outer)
      }
      ctx.closePath()
      ctx.fill()
      break
    }
    case 'heart': {
      const w = r
      ctx.moveTo(0, r * 0.3)
      ctx.bezierCurveTo(w, -r * 0.5, w * 1.2, r * 0.5, 0, r)
      ctx.bezierCurveTo(-w * 1.2, r * 0.5, -w, -r * 0.5, 0, r * 0.3)
      ctx.fill()
      break
    }
    case 'arrow': {
      ctx.lineWidth = Math.max(2, size / 8)
      ctx.moveTo(-r * 0.6, r * 0.3); ctx.lineTo(r * 0.4, -r * 0.3)
      ctx.stroke()
      const angle = Math.atan2(-r * 0.6, r)
      const head = size / 4
      ctx.beginPath()
      ctx.moveTo(r * 0.4, -r * 0.3)
      ctx.lineTo(r * 0.4 - head * Math.cos(angle - Math.PI / 6), -r * 0.3 - head * Math.sin(angle - Math.PI / 6))
      ctx.lineTo(r * 0.4 - head * Math.cos(angle + Math.PI / 6), -r * 0.3 - head * Math.sin(angle + Math.PI / 6))
      ctx.closePath()
      ctx.fill()
      break
    }
  }
  ctx.restore()
}

export function renderLaser(ctx: CanvasRenderingContext2D, x: number, y: number, color: string, ageMs: number) {
  // Decay over 1500ms — radius 28→0, alpha 0.9→0.
  const life = Math.max(0, 1 - ageMs / 1500)
  const radius = 28 * life
  if (radius <= 0) return
  ctx.save()
  ctx.globalAlpha = 0.9 * life
  ctx.fillStyle = color
  ctx.beginPath()
  ctx.arc(x, y, radius, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}
